/**
 * NavLinksClient Component
 *
 * Diamond Architecture: Client Component version of NavLinks.
 * Used in NavbarShell (Client Component).
 * Core navigation links (Rent / Buy) + optional section link.
 */
import { Link } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { routes, type Route } from '@letsgethome/routes';
import type { LocalizedText } from '@letsgethome/types/branded';

export interface SectionLink {
  /** Visible link text — must be `t()`-translated, never a raw literal. */
  label: LocalizedText;
  href: Route;
}

interface NavLinksClientProps {
  /** Optional section link (e.g., Blog, Estates) - shown as active */
  sectionLink?: SectionLink;
}

export function NavLinksClient({ sectionLink }: NavLinksClientProps) {
  const t = useTranslations('nav');
  const tA11y = useTranslations('common');

  const linkStyles =
    'rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';
  const activeLinkStyles = 'rounded-md px-3 py-2 text-sm font-medium text-foreground bg-muted';

  return (
    <nav aria-label={tA11y('a11y.primaryNavigation')}>
      <ul className="flex gap-1">
        <li>
          <Link href={routes.listings.rent()} prefetch="intent" className={linkStyles}>
            {t('rent')}
          </Link>
        </li>
        <li>
          <Link href={routes.listings.buy()} prefetch="intent" className={linkStyles}>
            {t('buy')}
          </Link>
        </li>
        {sectionLink && (
          <li>
            <Link href={sectionLink.href} prefetch="intent" className={activeLinkStyles}>
              {sectionLink.label}
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}
